import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  getPendingCustodyApprovals,
  submitCustodyDecision,
} from '../../api/custody';
import { Button } from '../../components/ui/Button';

const CustodyApprovals = () => {
  const { t } = useTranslation();
  const tr = useCallback((key, options) => t(`custodyApprovalsPage.${key}`, options), [t]);
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState({ type: 'idle', message: '' });
  const [actionState, setActionState] = useState({ id: null, decision: null });

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await getPendingCustodyApprovals();
      setRecords(data);
    } catch (err) {
      console.error('❌ Failed to fetch custody approvals:', err);
      setError(tr('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [tr]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleDecision = async (recordId, decision) => {
    const confirmation = window.confirm(
      tr('confirmDecision', { decision: tr(`decisions.confirm.${decision}`), id: recordId })
    );
    if (!confirmation) return;

    setActionState({ id: recordId, decision });
    setBanner({ type: 'idle', message: '' });

    try {
      await submitCustodyDecision(recordId, decision);
      setRecords((prev) => prev.filter((record) => record.id !== recordId));
      const successMessage = tr('success.decision', { decision: tr(`decisions.past.${decision}`) });
      setBanner({
        type: 'success',
        message: successMessage,
      });
      navigate('/request-submitted', {
        state: {
          title: tr('completionScreen.title', 'Request Action Completed Successfully!'),
          message: successMessage,
          requestId: recordId,
          requestType: tr('completionScreen.requestType', 'Custody Approval'),
          statusMessage: successMessage,
          nextApprover: tr('completionScreen.none', 'N/A'),
          pendingLevel: tr('completionScreen.none', 'N/A'),
        },
      });
    } catch (err) {
      console.error('❌ Failed to update custody record:', err);
      const message = err.response?.data?.message || tr('errors.decisionFailed');
      setBanner({ type: 'error', message });
    } finally {
      setActionState({ id: null, decision: null });
    }
  };

  const renderEmptyState = () => (
    <div className="text-center text-gray-500 py-10 border border-dashed rounded">
      {tr('emptyState')}
    </div>
  );

  const getActingRoleLabel = (role) =>
    role === 'hod' ? tr('roles.hod') : role === 'scm' ? tr('roles.scm') : tr('roles.custodian');

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">{t('pageTitles.custodyApprovals')}</h1>
          <Button variant="secondary" onClick={loadRecords} disabled={isLoading}>
            {tr('actions.refresh')}
          </Button>
        </div>

        {banner.type !== 'idle' && (
          <div
            className={`mb-4 rounded px-3 py-2 text-sm ${
              banner.type === 'success'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-600'
            }`}
          >
            {banner.message}
          </div>
        )}

        {error && <div className="text-red-600 mb-4">{error}</div>}

        {isLoading ? (
          <div className="text-gray-500">{tr('loading')}</div>
        ) : records.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className="space-y-4">
            {records.map((record) => {
              const actingRole = getActingRoleLabel(record.pending_role);
              return (
                <div key={record.id} className="bg-white shadow rounded-lg p-5 border">
                  <div className="flex flex-wrap justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold">{record.item_name}</h2>
                      <p className="text-sm text-gray-500">
                        {tr('fields.custodyType', { type: record.custody_type })}
                      </p>
                      {record.custody_code && (
                        <p className="text-sm text-gray-500">{tr('fields.code', { code: record.custody_code })}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {tr('fields.issuedBy', { name: record.issued_by_name || tr('fields.unknown') })}
                      </p>
                      <p className="text-sm text-gray-500">
                        {tr('fields.createdOn', {
                          date: new Date(record.created_at).toLocaleString(),
                        })}
                      </p>
                      <p className="text-sm font-medium text-indigo-600">
                        {tr('fields.awaitingAction', { role: actingRole })}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-sm text-gray-600">
                    <div>
                      <span className="font-semibold">{tr('fields.quantity')}</span> {record.quantity}
                    </div>
                    <div>
                      <span className="font-semibold">{tr('fields.department')}</span>{' '}
                      {record.custodian_department_name || tr('fields.notSpecified')}
                    </div>
                    <div>
                      <span className="font-semibold">{tr('fields.custodian')}</span>{' '}
                      {record.custodian_name || tr('fields.departmentCustody')}
                    </div>
                    <div>
                      <span className="font-semibold">{tr('fields.hod')}</span>{' '}
                      {record.hod_name || tr('fields.notAssigned')}
                    </div>
                  </div>

                  {record.description && (
                    <p className="mt-4 text-sm text-gray-700 border-t pt-3">
                      {record.description}
                    </p>
                  )}

                  <div className="mt-4 flex gap-3 flex-wrap">
                    <Button
                      onClick={() => handleDecision(record.id, 'approved')}
                      disabled={actionState.id === record.id}
                    >
                      {actionState.id === record.id && actionState.decision === 'approved'
                        ? tr('actions.approving')
                        : tr('actions.approve')}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDecision(record.id, 'rejected')}
                      disabled={actionState.id === record.id}
                    >
                      {actionState.id === record.id && actionState.decision === 'rejected'
                        ? tr('actions.rejecting')
                        : tr('actions.reject')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustodyApprovals;